/**
 * Icon colors for sidebar items, keyed by the name stored in a person's sidebar layout.
 *
 * `transparent` -- the default for every item -- is no tile at all: a plain icon in the row's own
 * text color, as the console's original sidebar drew it. Every other color is the background of a
 * small tile behind a white glyph, so each tone keeps at least 3:1 contrast against white. Those
 * values are literals rather than Tailwind classes because the console's Tailwind theme remaps the
 * stock palette (its `indigo-400` is red). They are accents, not surfaces, so unlike the slate ramp
 * they hold the same value in every theme.
 *
 * As with icons, a key must never be renamed or removed once shipped -- only added.
 */
export const NAV_COLORS = {
  transparent: 'transparent',
  red: '#dc2626',
  rose: '#e11d48',
  pink: '#db2777',
  fuchsia: '#c026d3',
  purple: '#9333ea',
  violet: '#7c3aed',
  indigo: '#4f46e5',
  blue: '#2563eb',
  sky: '#0284c7',
  cyan: '#0891b2',
  teal: '#0d9488',
  emerald: '#059669',
  green: '#16a34a',
  lime: '#4d7c0f',
  amber: '#d97706',
  orange: '#ea580c',
  brown: '#92400e',
  slate: '#475569'
} as const

export type NavColor = keyof typeof NAV_COLORS

/** What every item gets until someone picks a color for it, and what an unknown stored name falls back to. */
export const DEFAULT_NAV_COLOR: NavColor = 'transparent'

export function isNavColor (name: unknown): name is NavColor {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(NAV_COLORS, name)
}

/** Whether the item draws a colored tile, as opposed to a plain icon. */
export function hasNavTile (name: unknown): boolean {
  return isNavColor(name) && name !== 'transparent'
}

export function resolveNavColor (name: unknown): string {
  return NAV_COLORS[isNavColor(name) ? name : DEFAULT_NAV_COLOR]
}
